function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = []
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          "form",
          `${element.form.getAttribute("id")}-${uniqueKey}`
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open")
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    );
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*"
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === "minus"
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button):not(.menu-drawer__collapsed-button)"
    ).forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector("summary")
        )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              "transitionend",
              addTrapFocus
            );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`
    );
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset ||
      this.closest(".header-wrapper").classList.contains(
        "header-wrapper--border-bottom"
      )
        ? 1
        : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(
        this.header.getBoundingClientRect().bottom - this.borderOffset
      )}px`
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(
          this.header.getBoundingClientRect().bottom - this.borderOffset
        )}px`
      );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`
    );
  };
}

customElements.define("header-drawer", HeaderDrawer);

class HeaderDrawerAccordion extends MenuDrawer {
  connectedCallback() {
    const elements = document.querySelectorAll(
      '[data-action^="collapsed-links-"]'
    );

    elements.forEach((element) => {
      element.addEventListener("click", this.handleCollapsableBlock.bind(this));
    });
  }

  handleCollapsableBlock(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const action = button.dataset.action;
    const collapsedElement = this.querySelector(`[data-asset="${action}"]`);
    const isActive = event.currentTarget.classList.contains("active");

    if (!isActive) {
      const scrollHeight = collapsedElement.scrollHeight;
      button.classList.add("active");
      collapsedElement.style.height = `${scrollHeight}px`;

      setTimeout(() => {
        collapsedElement.style.height = "auto";
        collapsedElement.classList.add("show");
      }, 150);
    } else {
      const scrollHeight = collapsedElement.scrollHeight;
      
      requestAnimationFrame(() => {
        collapsedElement.style.height = `${scrollHeight}px`;
        void collapsedElement.offsetHeight;
        collapsedElement.style.height = "0";
        button.classList.remove("active");
        collapsedElement.classList.remove("show");
      });
    }
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset ||
      this.closest(".header-wrapper").classList.contains(
        "header-wrapper--border-bottom"
      )
        ? 1
        : 0;
    
    const headerBottom = this.header.getBoundingClientRect().bottom - this.borderOffset;
    
    requestAnimationFrame(() => {
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(headerBottom)}px`
      );
      this.header.classList.add("menu-open");
      document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    });

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    elementToFocus?.setAttribute("aria-expanded", false);
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    if (!this.header) return;
    
    const headerBottom = this.header.getBoundingClientRect().bottom - this.borderOffset;
    const viewportHeight = window.innerHeight;
    
    requestAnimationFrame(() => {
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(headerBottom)}px`
      );
      document.documentElement.style.setProperty(
        "--viewport-height",
        `${viewportHeight}px`
      );
    });
  };
}

customElements.define("header-drawer-accordion", HeaderDrawerAccordion);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false)
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            const sourceQty = html.querySelector(
              ".quick-order-list-container"
            ).parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(
        `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`
      )
    );
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(
        this.querySelector("template").content.firstElementChild.cloneNode(true)
      );

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(
        content.querySelector("video, model-viewer, iframe")
      );
      if (focus) deferredElement.focus();
      if (
        deferredElement.nodeName == "VIDEO" &&
        deferredElement.getAttribute("autoplay")
      ) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPage = 1;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.isInitialLoad = true;
    this.initialElementId = '';
    this.paginationContainer = this.querySelector('[data-slider-pagination]');
    this.useDots = !!this.paginationContainer;
    this.dots = [];
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchStartScrollLeft = 0;
    this.isVerticalTouch = null;
    this.sliderItemsToShow = [];
    
    this.cachedLayout = {
      sliderScrollLeft: 0,
      sliderClientWidth: 0,
      sliderScrollWidth: 0,
      slidePositions: new Map() 
    };
    
    this.updateScheduled = false;
    this.layoutCacheValid = false;

    if (!this.slider) return;

    this.initPages();
    const resizeObserver = new ResizeObserver(() => {
      if (this.initPagesTimeout) clearTimeout(this.initPagesTimeout);
      this.initPagesTimeout = setTimeout(() => {
        this.layoutCacheValid = false;
        this.initPages();
      }, 100);
    });
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", () => {
      if (!this.updateScheduled) {
        this.layoutCacheValid = false;
        this.updateScheduled = true;
        requestAnimationFrame(() => {
          this.update();
          this.updateScheduled = false;
        });
      }
    });
    
    if (this.prevButton) {
      this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    }
    if (this.nextButton) {
      this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
    }

    this.onTouchStartHandler = this.onTouchStart.bind(this);
    this.onTouchMoveHandler = this.onTouchMove.bind(this);
    this.onTouchEndHandler = this.onTouchEnd.bind(this);

    this.slider.addEventListener('touchstart', this.onTouchStartHandler, { passive: true });
    this.slider.addEventListener('touchmove', this.onTouchMoveHandler, { passive: true });
    this.slider.addEventListener('touchend', this.onTouchEndHandler, { passive: true });
    this.slider.addEventListener('touchcancel', this.onTouchEndHandler, { passive: true });
  }

  connectedCallback() {
    if (this.isInitialLoad) {
      this.isInitialLoad = false;
    }
  }

  cacheLayoutProperties() {
    this.cachedLayout.sliderScrollLeft = this.slider.scrollLeft;
    this.cachedLayout.sliderClientWidth = this.slider.clientWidth;
    this.cachedLayout.sliderScrollWidth = this.slider.scrollWidth;
    
    this.cachedLayout.slidePositions.clear();
    this.sliderItemsToShow?.forEach(element => {
      this.cachedLayout.slidePositions.set(element, {
        offsetLeft: element.offsetLeft,
        clientWidth: element.clientWidth
      });
    });
    
    this.layoutCacheValid = true;
  }

  initPages() {
    requestAnimationFrame(() => {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter(
        el => el.clientWidth > 0
      );

      if (this.sliderItemsToShow.length < 2) {
        if (this.useDots) {
          this.createDots();
          this.updateDots();
        }
        return;
      }

      const firstItemOffsetLeft = this.sliderItemsToShow[0].offsetLeft;
      const secondItemOffsetLeft = this.sliderItemsToShow[1].offsetLeft;
      const sliderClientWidth = this.slider.clientWidth;

      this.sliderItemOffset = secondItemOffsetLeft - firstItemOffsetLeft;

      this.slidesPerPage = Math.floor(
        (sliderClientWidth - firstItemOffsetLeft) / this.sliderItemOffset
      );

      this.totalPages = Math.max(
        1,
        this.sliderItemsToShow.length - this.slidesPerPage + 1
      );

      this.layoutCacheValid = false;
      
      this.update();
      if (this.useDots) {
        this.createDots();
        this.updateDots();
      }
    });
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.layoutCacheValid = false;
    this.initPages();
  }

  createDots() {
    if (!this.paginationContainer) return;

    this.paginationContainer.innerHTML = '';

    const slides = this.sliderItemsToShow || [];
    if (slides.length <= 1) {
      this.paginationContainer.setAttribute('hidden', 'true');
      this.paginationContainer.setAttribute('aria-hidden', 'true');
      this.dots = [];
      return;
    }

    this.paginationContainer.removeAttribute('hidden');
    this.paginationContainer.setAttribute('aria-hidden', 'false');
    // A group of buttons, not a tablist: a tablist must contain tabs, and these are plain "go to slide" buttons.
    this.paginationContainer.setAttribute('role', 'group');
    this.paginationContainer.setAttribute('aria-label', window.theme?.strings?.sliderDotsLabel || 'Slides');

    const fragment = document.createDocumentFragment();
    this.dots = slides.map((slide, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'slider-dot';
      button.setAttribute('data-slide-index', index + 1);
      button.setAttribute('aria-label', `${window.theme?.strings?.sliderDot || 'Go to slide'} ${index + 1}`);
      button.addEventListener('click', () => this.goToSlide(index));
      const hiddenText = document.createElement('span');
      hiddenText.className = 'visually-hidden';
      hiddenText.textContent = `${window.theme?.strings?.sliderDot || 'Slide'} ${index + 1}`;
      button.appendChild(hiddenText);
      fragment.appendChild(button);
      return button;
    });

    this.paginationContainer.appendChild(fragment);
  }

  updateDots() {
    if (!this.dots || !this.dots.length) return;

    requestAnimationFrame(() => {
      this.dots.forEach((dot, index) => {
        const isActive = index === this.currentPage - 1;
        dot.classList.toggle('is-active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    });
  }

  goToSlide(index) {
    const targetSlide = this.sliderItemsToShow?.[index];
    if (!targetSlide) return;

    const firstSlide = this.sliderItemsToShow[0];
    if (!firstSlide) return;

    requestAnimationFrame(() => {
      const offset = targetSlide.offsetLeft - firstSlide.offsetLeft;
      this.setSlidePosition(offset);
    });
  }

  onTouchStart(event) {
    if (!event.touches || !event.touches.length) return;
    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchStartScrollLeft = this.slider.scrollLeft;
    this.isVerticalTouch = null;
  }

  onTouchMove(event) {
    if (!event.touches || !event.touches.length) return;
    const touch = event.touches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;

    if (this.isVerticalTouch === null) {
      const directionThreshold = 10;
      if (Math.abs(deltaY) > Math.abs(deltaX) + directionThreshold) {
        this.isVerticalTouch = true;
      } else if (Math.abs(deltaX) > Math.abs(deltaY) + directionThreshold) {
        this.isVerticalTouch = false;
      } else {
        return;
      }
    }

    if (this.isVerticalTouch) {
      this.slider.scrollLeft = this.touchStartScrollLeft;
    }
  }

  onTouchEnd() {
    this.isVerticalTouch = null;
  }

  update() {
    if (!this.slider) return;

    if (!this.layoutCacheValid) {
      this.cacheLayoutProperties();
    }

    const allSlides = this.slider.querySelectorAll('li[id^="Slide-"]');
    const productGallerySlides = [];

    allSlides.forEach(item => {
      if (item.classList.contains('product__media-item')) {
        productGallerySlides.push(item);
      }
    });

    const slidesToEvaluate = productGallerySlides.length ? productGallerySlides : Array.from(this.sliderItemsToShow);

    const previousPage = this.currentPage;

    let visibleSlideIndex = slidesToEvaluate.findIndex((item) =>
      this.isSlideVisible(item)
    );

    // Sub-pixel rounding on mobile (especially Chrome on Android with snap-scroll)
    // can leave every slide failing the strict bounds check. Fall back to the
    // slide whose left edge is closest to the current scrollLeft so a slide is
    // always selected — otherwise the video slide never gets is-active and the
    // featured PDP video fails to autoplay.
    if (visibleSlideIndex < 0 && slidesToEvaluate.length) {
      const scrollLeft = this.layoutCacheValid
        ? this.cachedLayout.sliderScrollLeft
        : this.slider.scrollLeft;
      let minDist = Infinity;
      slidesToEvaluate.forEach((item, idx) => {
        const cached = this.layoutCacheValid && this.cachedLayout.slidePositions.has(item)
          ? this.cachedLayout.slidePositions.get(item)
          : null;
        const offsetLeft = cached ? cached.offsetLeft : item.offsetLeft;
        const dist = Math.abs(offsetLeft - scrollLeft);
        if (dist < minDist) {
          minDist = dist;
          visibleSlideIndex = idx;
        }
      });
    }

    const visibleSlideFound = visibleSlideIndex >= 0;
    this.currentPage = visibleSlideFound ? visibleSlideIndex + 1 : previousPage;

    const videoSlide = this.slider.querySelector('li[data-type="product-video"]');
    const slidesForActiveState = productGallerySlides.length ? productGallerySlides : Array.from(this.sliderItemsToShow);

    const hasActive = slidesForActiveState.some(item =>
      item.classList.contains('is-active')
    );

    if (!hasActive && this.isInitialLoad) {
      slidesForActiveState.forEach((item, index) => {
        if (index === 0) {
          this.initialElementId = item.id;
        }
      });
    }

    requestAnimationFrame(() => {
      if (visibleSlideFound) {
        slidesForActiveState.forEach((item, index) => {
          const isActive = index === visibleSlideIndex;
          item.classList.toggle('is-active', isActive);

          if (item === videoSlide) {
            this.manageVideo(isActive);
          }
        });
      }

      if (this.currentPageElement && this.pageTotalElement) {
        this.currentPageElement.textContent = this.currentPage;
        this.pageTotalElement.textContent = this.totalPages;
      }

      this.updateDots();

      if (this.currentPage !== previousPage) {
        this.dispatchEvent(
          new CustomEvent("slideChanged", {
            detail: {
              currentPage: this.currentPage,
              currentElement: this.sliderItemsToShow[this.currentPage - 1],
            },
          })
        );
      }

      if (this.enableSliderLooping) return;

      const maxScrollLeft = this.cachedLayout.sliderScrollWidth - this.cachedLayout.sliderClientWidth;

      if (this.prevButton && this.sliderItemsToShow.length) {
        if (
          this.isSlideVisible(this.sliderItemsToShow[0]) &&
          this.cachedLayout.sliderScrollLeft === 0
        ) {
          this.prevButton.setAttribute("disabled", "disabled");
        } else {
          this.prevButton.removeAttribute("disabled");
        }
      }

      if (this.nextButton) {
        if (this.cachedLayout.sliderScrollLeft >= maxScrollLeft - 1) {
          this.nextButton.setAttribute("disabled", "disabled");
        } else {
          this.nextButton.removeAttribute("disabled");
        }
      }
    });
  }

  manageVideo(check) {
    const videoSlide = document.querySelector('li[data-type="product-video"]');
    if (videoSlide) {
      const video = videoSlide.querySelector('video');
      if (check) {
        if (video) {
          video.currentTime = 0;
          video.setAttribute('loop', '');
          video.setAttribute('autoplay', '');
          requestAnimationFrame(() => {
            setTimeout(() => {
              video.play().catch((err) => {
                console.warn('[product-video] autoplay failed (slider)', {
                  name: err && err.name,
                  message: err && err.message,
                  readyState: video.readyState,
                  networkState: video.networkState,
                  muted: video.muted,
                  paused: video.paused,
                });
              });
            }, 50);
          });
        }
      } else {
        if (video) {
          video.pause();
          video.currentTime = 0;
          video.removeAttribute('autoplay');
          video.removeAttribute('loop');
        }
      }
    }
  }

  // Maintained for external use - now uses cached values
  isSlideVisible(element, offset = 0) {
    const sliderScrollLeft = this.layoutCacheValid 
      ? this.cachedLayout.sliderScrollLeft 
      : this.slider.scrollLeft;
      
    const sliderClientWidth = this.layoutCacheValid 
      ? this.cachedLayout.sliderClientWidth 
      : this.slider.clientWidth;

    let elementOffsetLeft, elementClientWidth;
    
    if (this.layoutCacheValid && this.cachedLayout.slidePositions.has(element)) {
      const cached = this.cachedLayout.slidePositions.get(element);
      elementOffsetLeft = cached.offsetLeft;
      elementClientWidth = cached.clientWidth;
    } else {
      elementOffsetLeft = element.offsetLeft;
      elementClientWidth = element.clientWidth;
    }

    const lastVisibleSlide = sliderClientWidth + sliderScrollLeft - offset;
    const elementRight = elementOffsetLeft + elementClientWidth;

    // Allow a small tolerance to absorb sub-pixel rounding from scroll-snap on
    // high-DPR mobile screens, where scrollLeft/offsetLeft can disagree by
    // ~0.3–1px even when a slide is fully snapped into view.
    const tolerance = 2;
    const isVisible =
      elementClientWidth > 0 &&
      elementRight <= lastVisibleSlide + tolerance &&
      elementOffsetLeft >= sliderScrollLeft - tolerance;

    return isVisible;
  }

  onButtonClick(event) {
    event.preventDefault();
    
    const buttonName = event.currentTarget.name;
    const step = Number(event.currentTarget.dataset.step || 1);

    requestAnimationFrame(() => {
      const scrollLeft = this.slider.scrollLeft;
      const scrollWidth = this.slider.scrollWidth;
      const clientWidth = this.slider.clientWidth;

      if (buttonName === "next") {
        if (
          this.enableSliderLooping &&
          this.currentPage >= this.totalPages
        ) {
          this.slideScrollPosition = 0;
        } else {
          this.slideScrollPosition = scrollLeft + step * this.sliderItemOffset;
        }
      } else {
        if (
          this.enableSliderLooping &&
          this.currentPage === 1
        ) {
          this.slideScrollPosition = scrollWidth - clientWidth;
        } else {
          this.slideScrollPosition = scrollLeft - step * this.sliderItemOffset;
        }
      }

      this.layoutCacheValid = false; 
      this.setSlidePosition(this.slideScrollPosition);
    });
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
      behavior: "smooth",
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;
    this.activeIndex = 0;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link")
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this))
    );
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      );
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true")
          this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true")
      this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    // Pause when tab is hidden, resume when visible to prevent timer drift
    this.visibilityChangeHandler = () => {
      if (document.hidden) {
        this.pause();
      } else if (!this.reducedMotion?.matches) {
        // Don't resume if user manually paused via the autoplay button
        if (this.sliderAutoplayButton && !this.autoplayButtonIsSetToPlay) return;
        this.play();
      }
    };
    document.addEventListener("visibilitychange", this.visibilityChangeHandler);

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener(
        "click",
        this.autoPlayToggle.bind(this)
      );
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add(
      "slider-counter__link--active"
    );
    this.sliderControlButtons[this.currentPage - 1].setAttribute(
      "aria-current",
      true
    );
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed
    );
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow
      );
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow
      );
    }
  }

  autoRotateSlides() {
    // Sync activeIndex with scroll-derived currentPage to prevent desync
    if (this.announcementBarSlider) {
      this.activeIndex = this.currentPage - 1;
    }

    // If it's the last slide, scroll to the first slide
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0 // Loop back to the first slide
        : this.slider.scrollLeft + this.sliderItemOffset; // Continue to the next slide

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
  if (!this.announcementBarSlider || !this.sliderItems.length) return;

  const itemsCount = this.sliderItems.length;
  const increment = button === "next" ? 1 : -1;

  // Update index globally
  this.activeIndex = (this.activeIndex + increment + itemsCount) % itemsCount;

  const nextIndex = this.activeIndex;
  const prevIndex = (nextIndex - increment + itemsCount) % itemsCount;

  const nextSlide = this.sliderItems[nextIndex];
  const currentSlide = this.sliderItems[prevIndex];

  const animationClassIn = "announcement-bar-slider--fade-in";
  const animationClassOut = "announcement-bar-slider--fade-out";
  const direction = increment > 0 ? "next" : "previous";

  this.sliderItems.forEach((item) => {
    if (item.style.display) {
      item.style.removeProperty("display");
    }
  });

  currentSlide.classList.add(`${animationClassOut}-${direction}`);
  nextSlide.classList.add(`${animationClassIn}-${direction}`);

  setTimeout(() => {
    currentSlide.classList.remove(`${animationClassOut}-${direction}`);
    nextSlide.classList.remove(`${animationClassIn}-${direction}`);
  }, this.announcerBarAnimationDelay * 2);
}

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) +
          1 -
          this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        .querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          swatchValue
        );
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          "unset"
        );
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset"
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector("[data-selected-value]");
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked")
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define("variant-selects", VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (
          !this.querySelector("slideshow-component") &&
          this.classList.contains("complementary-products")
        ) {
          this.remove();
        }

        if (html.querySelector(".grid__item")) {
          this.classList.add("product-recommendations--loaded");
        }

        // Recommended product click tracking
        if (Shopify && Shopify.analytics) {
          const recommendedProducts = this.querySelectorAll(
            "a[data-recommended]"
          );
          recommendedProducts?.forEach((recommend) => {
            recommend?.addEventListener("click", () => {
              const variantSku = recommend.dataset?.variantSku;

              window.Shopify?.analytics?.publish?.("recommended_product_click", {
                product_id: productId,
                variant_sku: variantSku,
              });
            });
          });
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener(
      "storefront:signincompleted",
      this.handleStorefrontSignInCompleted.bind(this)
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter(
      (queueElement) => !queue.includes(queueElement)
    );
    const quickBulkElement =
      this.closest("quick-order-list") || this.closest("quick-add-bulk");
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.min_error.replace(
          "[min]",
          event.target.dataset.min
        )
      );
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.max_error.replace(
          "[max]",
          event.target.max
        )
      );
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.step_error.replace(
          "[step]",
          event.target.step
        )
      );
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}

/*
  A stock check BC hasn't refreshed is behind by the working days since its sync, so its first
  available date is pushed on by that many working days, up to 3 (Rich, 20 Oct 2026). BC counts
  Monday to Friday and skips bank holidays (Luis, 30 Oct 2026; the list is gov.uk's for England and Wales, add each
  year, and keep it the same as BANK_HOLIDAYS in Swyft-Delivery-Dates); calendar days fell a day short over a weekend (SWYFT9372605, 29 Oct 2026). Older, or no sync
  stamp, is left alone. The stamp (updatedAt in swyft.variant_stock_check) and "today" are
  both UK dates. Must match withStaleUplift in the checkout picker and staleDays in the
  delivery function (Swyft-Delivery-Dates), so the product page and checkout agree.
  On a preview theme only, ?stock_stale=N treats every stock check as N days old.
*/
window.swyftStaleUplift = (firstAvailableDate, syncedAt) => {
  const day = (value) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ""));
    return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) / 86400000 : null;
  };
  const stored = day(firstAvailableDate);
  let synced = day(syncedAt);
  const today = day(new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London" }).format(new Date()));
  const simulated = window.Shopify?.theme?.role !== "main" && parseInt(new URLSearchParams(location.search).get("stock_stale"), 10);
  if (simulated > 0 && today !== null) synced = today - simulated;
  if (stored === null || synced === null || today === null) return firstAvailableDate;
  const holidays = [
    "2026-01-01", "2026-04-03", "2026-04-06", "2026-05-04", "2026-05-25", "2026-08-31", "2026-12-25", "2026-12-28",
    "2027-01-01", "2027-03-26", "2027-03-29", "2027-05-03", "2027-05-31", "2027-08-30", "2027-12-27", "2027-12-28",
    "2028-01-03", "2028-04-14", "2028-04-17", "2028-05-01", "2028-05-29", "2028-08-28", "2028-12-25", "2028-12-26",
  ].map(day);
  // Day 0 is Thursday 1 January 1970.
  const working = (d) => ![0, 6].includes((d + 4) % 7) && !holidays.includes(d);
  let days = 0;
  for (let d = synced + 1; d <= today; d++) if (working(d)) days++;
  if (days < 1 || days > 3) return firstAvailableDate;
  let moved = stored;
  for (let n = days; n > 0; ) if (working(++moved)) n--;
  return new Date(moved * 86400000).toISOString().slice(0, 10);
};

class ItemAvailability extends HTMLElement {
  constructor() {
    super();
    this.cutoffIntervalId = null;
    this.deliveryMessages =
      ItemAvailability.getDeliveryMessages() || null;
    this.setupElementReferences();
    this.configureStateFromAttributes();
  }

  static getDeliveryMessages() {
    if (typeof ItemAvailability.deliveryMessagesCache !== "undefined") {
      return ItemAvailability.deliveryMessagesCache;
    }
    try {
      const element = document.querySelector("[data-delivery-messages]");
      ItemAvailability.deliveryMessagesCache = element
        ? JSON.parse(element.textContent || "{}")
        : null;
    } catch (err) {
      ItemAvailability.deliveryMessagesCache = null;
    }
    return ItemAvailability.deliveryMessagesCache;
  }

  setupElementReferences() {
    this.deliveryMessageElement = this.querySelector(
      '[data-message="delivery"]'
    );
    this.deliveryChoiceElement = this.querySelector(
      '[data-message="delivery-choice"]'
    );
    this.cutoffTimeMessageElement = this.querySelector(
      '[data-message="cut-off-time"]'
    );
    this.qtyAvailabilityMessageElement = this.querySelector(
      '[data-message="qty-availability"]'
    );
    this.christmasElement = this.querySelector('[data-message="christmas"]');
  }

  configureStateFromAttributes() {
    this.displayMode = this.dataset.display || "full";
    this.addedQty = parseInt(this.dataset?.addedQty, 10) || 0;
    this.isGiftCard = this.resolveBooleanFlag(this.dataset.isGiftCard, false);
    this.stockData = this.parseStockPayload(this.dataset?.stock);

    if (!this.stockData && (this.dataset?.firstAvailableDate || this.dataset?.stockQty)) {
      this.stockData = {
        firstAvailableDate: this.dataset.firstAvailableDate || "",
        stock: parseInt(this.dataset?.stockQty, 10) || 0,
      };
    }

    if (this.stockData) {
      if (
        typeof this.stockData.stock === "undefined" ||
        this.stockData.stock === null ||
        this.stockData.stock === ""
      ) {
        this.stockData.stock =
          parseInt(this.dataset?.stockQty, 10) ||
          parseInt(this.stockData.stock, 10) ||
          0;
      }
    } else {
      this.stockData = null;
    }

    this.christmasText = (this.dataset.christmasText || "").trim();
    this.christmasLastDate = this.dataset.christmasLastDate || "";

    const defaults = ItemAvailability.defaultStrings;
    this.nextDayText = this.normalizeTranslation(
      this.dataset.nextDayText,
      undefined,
      "Available next day"
    );
    this.deliveryFromTemplate = this.normalizeTranslation(
      this.dataset.deliveryFromTemplate,
      undefined,
      "Delivery from __DATE__"
    );
    this.outOfStockText = this.normalizeTranslation(
      this.dataset.outOfStockText,
      undefined,
      "Out of stock"
    );
    this.notifyText = this.normalizeTranslation(
      this.dataset.notifyText,
      undefined,
      "Notify me"
    );
    this.formattedDateTemplate = this.normalizeTranslation(
      this.dataset.formattedDateTemplate,
      undefined,
      "{{ day }}{{ ordinal }} {{ month }}"
    );
    this.giftCardText = this.normalizeTranslation(
      this.dataset.giftCardText,
      undefined,
      'Send immediately, or choose a future date'
    )

    this.showStockMessage = this.resolveBooleanFlag(
      this.dataset.showStockMessage,
      this.displayMode !== "text"
    );
    this.showCutoffMessage = this.resolveBooleanFlag(
      this.dataset.showCutoffMessage,
      this.displayMode !== "text"
    );
    this.deliveryChoiceText = this.normalizeTranslation(
      this.dataset.deliveryChoiceText,
      undefined,
      ", or a date of your choice"
    );
    this.showDeliveryChoice = this.resolveBooleanFlag(
      this.dataset.showDeliveryChoice,
      false
    );

    if (this.displayMode === "text") {
      this.showStockMessage = false;
      this.showCutoffMessage = false;
    }

    this.locale =
      this.dataset.language &&
      this.dataset.language !== "" &&
      this.dataset.country &&
      this.dataset.country !== ""
        ? `${this.dataset.language.toLocaleLowerCase()}-${this.dataset.country.toLocaleUpperCase()}`
        : "en-GB";

    this.excludedDeliveryDates = this.parseExcludedDates(
        this.dataset.excludedDeliveryDates
    );
  }

  resolveBooleanFlag(value, defaultValue) {
    if (typeof value === "undefined") return defaultValue;
    return value === "true" || value === "1";
  }

  normalizeTranslation(value, fallback, defaultValue) {
    const candidate = value || fallback || "";
    if (!candidate) return defaultValue;
    if (
      typeof candidate === "string" &&
      candidate.includes("translation missing")
    ) {
      return defaultValue;
    }
    return candidate;
  }

  parseStockPayload(raw) {
    if (!raw || raw === "null" || raw === "") return null;
    try {
      const payload =
        typeof raw === "string" ? JSON.parse(raw.replace(/=>/g, ":")) : raw;
      return payload;
    } catch (err) {
      return null;
    }
  }

  parseExcludedDates(raw) {
    if (!raw || raw === "null" || raw === "") return [];

    try {
      const dates = raw.split(',').map(d => d.trim()).filter(Boolean);
      return dates.map(dateStr => {
        const date = new Date(dateStr);
        date.setHours(0, 0, 0, 0);
        return date;
      }).filter(date => !isNaN(date.getTime()));
    } catch (err) {
      console.error('Error parsing excluded delivery dates:', err);
      return [];
    }
  }

  isDateExcluded(date) {
    if (!this.excludedDeliveryDates?.length) return false;
    
    const checkTime = new Date(date).setHours(0, 0, 0, 0);
    
    return this.excludedDeliveryDates.some(
      excludedDate => excludedDate.getTime() === checkTime
    );
  }

  getNextNonExcludedDate(startDate) {
    let date = new Date(startDate);
    let iterations = 0;
    const maxIterations = 365; // Prevent infinite loops
    
    while (this.isDateExcluded(date) && iterations < maxIterations) {
      date.setDate(date.getDate() + 1);
      iterations++;
    }
    
    return date;
  }

  connectedCallback() {
    this.refresh();
  }

  refresh(overrides = {}) {
    this.applyOverridesToDataset(overrides);
    this.configureStateFromAttributes();
    this.renderAvailability();
  }

  applyOverridesToDataset(overrides) {
    if (!overrides || typeof overrides !== "object") return;
    if (
      Object.prototype.hasOwnProperty.call(overrides, "stockData") &&
      overrides.stockData
    ) {
      this.dataset.stock = JSON.stringify(overrides.stockData);
    }
    if (Object.prototype.hasOwnProperty.call(overrides, "stockQty")) {
      const nextQty = overrides.stockQty;
      if (nextQty === null || typeof nextQty === "undefined") {
        delete this.dataset.stockQty;
      } else {
        this.dataset.stockQty = String(nextQty);
      }
    }
    if (Object.prototype.hasOwnProperty.call(overrides, "firstAvailableDate")) {
      const nextDate = overrides.firstAvailableDate;
      if (nextDate === null || typeof nextDate === "undefined") {
        delete this.dataset.firstAvailableDate;
      } else {
        this.dataset.firstAvailableDate = nextDate;
      }
    }
    if (Object.prototype.hasOwnProperty.call(overrides, "showCutoffMessage")) {
      this.dataset.showCutoffMessage = overrides.showCutoffMessage
        ? "true"
        : "false";
    }
    if (Object.prototype.hasOwnProperty.call(overrides, "showStockMessage")) {
      this.dataset.showStockMessage = overrides.showStockMessage
        ? "true"
        : "false";
    }
  }

  renderAvailability() {
    this.clearCutoffInterval();

    const stock = this.normalizeStock();
    const hasDeliveryMessage = this.updateDeliveryMessage(stock);
    this.updateChristmasMessage(stock);

    const hasFutureDate = Boolean(this.stockData?.firstAvailableDate);

    if (stock <= 0) {
      this.setOutOfStockStyles();
      if (this.qtyAvailabilityMessageElement) {
        this.qtyAvailabilityMessageElement.textContent = "";
        this.qtyAvailabilityMessageElement.classList.remove(
          "less-stock",
          "more-stock"
        );
      }
      this.updateCutOffMessages(false);
      this.updateDeliveryChoice(false);
      return;
    }

    if (
      this.showStockMessage &&
      this.deliveryMessages &&
      this.qtyAvailabilityMessageElement
    ) {
      this.updateStockMessage(stock);
    } else if (this.qtyAvailabilityMessageElement) {
      this.qtyAvailabilityMessageElement.textContent = "";
      this.qtyAvailabilityMessageElement.classList.remove(
        "less-stock",
        "more-stock"
      );
    }

    if (this.cutoffTimeMessageElement && this.showCutoffMessage) {
      this.updateCutoffTimeMessage();
      this.cutoffIntervalId = setInterval(
        () => this.updateCutoffTimeMessage(),
        1000
      );
    } else {
      this.updateCutOffMessages(false);
    }

  this.updateDeliveryChoice(hasDeliveryMessage);
  }

  clearCutoffInterval() {
    if (this.cutoffIntervalId) {
      clearInterval(this.cutoffIntervalId);
      this.cutoffIntervalId = null;
    }
  }

  disconnectedCallback() {
    this.clearCutoffInterval();
  }

  formatMessage(template, values) {
    if (!template || template == "" || !values) {
      return "";
    }
    return template.replace(
      /\$\{(\w+)\}/g,
      (match, key) => values[key] || match
    );
  }

  normalizeStock() {
    let stockContainer = this.stockData;
    if (!stockContainer || typeof stockContainer !== "object") {
      stockContainer = {};
    }

    const fallbackStock = parseInt(this.dataset?.stockQty, 10);
    const rawStock =
      typeof stockContainer.stock !== "undefined"
        ? stockContainer.stock
        : fallbackStock;
    const parsedStock = parseInt(rawStock, 10);
    const stock = Number.isNaN(parsedStock) ? 0 : parsedStock;

    stockContainer.stock = stock;

    if (
      !stockContainer.firstAvailableDate &&
      this.dataset?.firstAvailableDate
    ) {
      stockContainer.firstAvailableDate = this.dataset.firstAvailableDate;
    }

    this.stockData = stockContainer;
    return stock;
  }

  setOutOfStockStyles() {
    if (this.deliveryMessageElement) {
      const message = this.outOfStockText || "";
      this.deliveryMessageElement.textContent = message;
      this.deliveryMessageElement.classList.toggle(
        "is-visible",
        Boolean(message)
      );
      this.deliveryMessageElement.classList.remove("is-next-day");
    }
    if (this.qtyAvailabilityMessageElement) {
      this.qtyAvailabilityMessageElement.classList.remove(
        "more-stock",
        "less-stock"
      );
      this.qtyAvailabilityMessageElement.classList.add("less-stock");
      this.qtyAvailabilityMessageElement.textContent = "";
    }
    this.isShowingNextDay = false;
  }

  replaceDateTemplate(template, replacement) {
    if (!template) return replacement;
    if (!replacement) return template;
    return template.replace(/__DATE__/g, replacement);
  }

  composeFallbackDeliveryMessage({ deliveryDateText, isNextDayWindow }) {
    if (isNextDayWindow && this.nextDayText) {
      return this.nextDayText;
    }
    if (!deliveryDateText) {
      return "";
    }

    const templated = this.replaceDateTemplate(
      this.deliveryFromTemplate,
      deliveryDateText
    );

    if (templated && templated !== "") {
      return templated;
    }

    return `Delivery from ${deliveryDateText}`;
  }

  updateStockMessage(stock) {
    if (
      !this.deliveryMessages ||
      !this.deliveryMessages.stock_message ||
      !this.qtyAvailabilityMessageElement
    ) {
      return;
    }

    this.qtyAvailabilityMessageElement.classList.remove(
      "less-stock",
      "more-stock"
    );

    const className = stock < 20 ? "less-stock" : "more-stock";
    let message = "";
    if (stock > 0 && stock < 20) {
      message = this.formatMessage(
        this.deliveryMessages.stock_message.available_stock,
        { stock }
      );
    } else if (stock >= 20) {
      const inStockTemplate = this.deliveryMessages.stock_message.in_stock;
      message =
        inStockTemplate && inStockTemplate !== ""
          ? inStockTemplate
          : "";
    }

    this.qtyAvailabilityMessageElement.classList.add(className);
    this.qtyAvailabilityMessageElement.textContent = message || "";
  }

  updateDeliveryMessage(stock) {
    if (!this.deliveryMessageElement) return false;
    const deliveryElement = this.deliveryMessageElement;
    const normalizedNextDayText = (this.nextDayText || "Available next day").trim().toLowerCase();

    if (this.isGiftCard) {
      const message = this.giftCardText || "";
      deliveryElement.textContent = message;
      deliveryElement.classList.toggle("is-visible", Boolean(message));
      deliveryElement.classList.remove("is-next-day");
      this.isShowingNextDay = false;
      return Boolean(message);
    }

    if (stock <= 0) {
      const message = this.outOfStockText || "";
      deliveryElement.textContent = message;
      deliveryElement.classList.toggle("is-visible", Boolean(message));
      deliveryElement.classList.remove("is-next-day");
      this.isShowingNextDay = false;
      return Boolean(message);
    }

    const storeCutoffTime = this.getStoreCutoffTime();
    const today = this.getCurrentDate();
    const tomorrow = this.getTomorrowDate();

    const deliveryDate = this.getDeliveryDate();
    const hasValidDeliveryDate =
      deliveryDate && deliveryDate instanceof Date;
    const deliveryDateText = hasValidDeliveryDate
      ? this.getDeliveryDateText(deliveryDate)
      : "";
    const futureThreshold = this.getCurrentDate(today);
    futureThreshold.setDate(today.getDate() + 4);

    const isNextDayWindow =
      hasValidDeliveryDate &&
      storeCutoffTime &&
      tomorrow &&
      deliveryDate <= tomorrow &&
      tomorrow < storeCutoffTime;
    const isEligibleForNextDay =
      Boolean(isNextDayWindow && stock > 0 && hasValidDeliveryDate);

    let stockMessage = "";
    this.isShowingNextDay = isEligibleForNextDay;

    if (
      this.deliveryMessages &&
      this.deliveryMessages.available_messages &&
      hasValidDeliveryDate
    ) {
      const showStockCounts = this.showStockMessage;
      const messages = this.deliveryMessages.available_messages;

      if (deliveryDate < futureThreshold) {
        const deliveryFrom = isNextDayWindow
          ? "tomorrow"
          : deliveryDateText;

        if (stock <= 0) {
          stockMessage = this.formatMessage(
            messages.future_delivery_message,
            { delivery_date: deliveryDateText }
          );
        } else if (isEligibleForNextDay) {
          stockMessage = this.nextDayText || "Available next day";
        } else if (stock < 10 && showStockCounts) {
          stockMessage = this.formatMessage(
            messages.minimum_qty_message,
            { stock, delivery_date: deliveryFrom }
          );
        } else {
          stockMessage = this.formatMessage(
            messages.in_stock_message,
            { delivery_date: deliveryFrom }
          );
        }
      } else {
        stockMessage = this.formatMessage(
          messages.future_delivery_message,
          { delivery_date: deliveryDateText }
        );
      }
    } else if (hasValidDeliveryDate) {
      stockMessage = this.composeFallbackDeliveryMessage({
        deliveryDateText,
        isNextDayWindow,
      });
    }

    const hasMessage = Boolean(stockMessage && stockMessage !== "");
    const isNextDayMessage =
      this.isShowingNextDay &&
      Boolean(
        stockMessage &&
        stockMessage.trim().toLowerCase() === normalizedNextDayText
      );

    if (hasMessage) {
      deliveryElement.innerHTML = stockMessage;
    } else {
      deliveryElement.textContent = "";
    }

    deliveryElement.classList.toggle("is-visible", hasMessage);
    deliveryElement.classList.toggle("is-next-day", isNextDayMessage);
    return hasMessage;
  }

  // Only when this item's own delivery date makes it; the last date itself is never shown.
  updateChristmasMessage(stock) {
    const element = this.christmasElement;
    if (!element) return;
    const lastDate = this.christmasLastDate ? new Date(`${this.christmasLastDate}T23:59:59`) : null;
    const deliveryDate = stock > 0 && !this.isGiftCard ? this.getDeliveryDate() : null;
    const show = Boolean(
      this.christmasText &&
      lastDate && !isNaN(lastDate) &&
      deliveryDate instanceof Date && deliveryDate <= lastDate
    );
    element.textContent = show ? this.christmasText : "";
    element.hidden = !show;
  }

  updateDeliveryChoice(show) {
    if (!this.deliveryChoiceElement || this.isGiftCard) return;

    const shouldShow = Boolean(
      this.showDeliveryChoice &&
        show &&
        this.deliveryChoiceText &&
        this.deliveryChoiceText.trim() !== ""
    );

    if (shouldShow) {
      this.deliveryChoiceElement.textContent = this.deliveryChoiceText;
    } else {
      this.deliveryChoiceElement.textContent = "";
    }

    this.deliveryChoiceElement.classList.toggle("is-visible", shouldShow);
    this.deliveryChoiceElement.style.display = shouldShow ? "" : "none";
    this.deliveryChoiceElement.setAttribute(
      "aria-hidden",
      shouldShow ? "false" : "true"
    );
  }

  updateCutOffMessages(show) {
    if (!this.cutoffTimeMessageElement) return;
    const container = this.cutoffTimeMessageElement.closest(
      '[data-id="cut-off-message"]'
    );
    if (!container) return;
    container.style.display = show ? "" : "none";
  }

  updateCutoffTimeMessage() {
    const storeCutoffTime = this.getStoreCutoffTime();

    const now = this.getCurrentDate();
    const timeLeft = storeCutoffTime - now;
    if (timeLeft > 0) {
      const hours = Math.floor((timeLeft / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((timeLeft / (1000 * 60)) % 60);
      const seconds = Math.floor((timeLeft / 1000) % 60);

      this.cutoffTimeMessageElement.innerHTML = `<span class="time-count">${hours} Hrs ${minutes} Mins ${seconds} Secs</span>`;
      this.updateCutOffMessages(true);
    } else {
      this.updateCutOffMessages(false);
    }
  }

  getCurrentDate(date = null) {
    return date ? new Date(date) : new Date();
  }

  getTomorrowDate() {
    const tomorrow = this.getCurrentDate();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  }

  getDeliveryDate() {
    const firstAvailableDate = this.getFirstAvailableDate();
    if (!(firstAvailableDate instanceof Date)) return;

    const cutoffHour = this.getStoreCutOffHour();
    const cutoffMinute = this.getStoreCutOffMinute();
    const today = this.getCurrentDate();
    const currentHour = today.getHours();
    const currentMinute = today.getMinutes();
    const isCrossedStoreClosingTime =
      currentHour > cutoffHour ||
      (currentHour === cutoffHour && currentMinute > cutoffMinute);

    const diffInTime = firstAvailableDate.getTime() - today.getTime();
    const diffInDays = Math.floor(diffInTime / (1000 * 60 * 60 * 24));

    let deliveryDate;
    deliveryDate = this.getNextDate(firstAvailableDate);

    const day = firstAvailableDate.getDay();

    if (diffInDays <= 2) {
      if (day >= 1 && day <= 4 && isCrossedStoreClosingTime) {
        deliveryDate = this.getNextDate(deliveryDate);
      } else if (day == 5) {
        deliveryDate.setDate(
          deliveryDate.getDate() + (isCrossedStoreClosingTime ? 3 : 0)
        );
      } else if (day == 6) {
        deliveryDate.setDate(deliveryDate.getDate() + 2);
      } else if (day == 0) {
        deliveryDate.setDate(
          deliveryDate.getDate() + (isCrossedStoreClosingTime ? 2 : 1)
        );
      }
    }

    if (deliveryDate.getDay() === 0) {
      deliveryDate.setDate(deliveryDate.getDate() + 1);
    }

    deliveryDate = this.getNextNonExcludedDate(deliveryDate);
    
    if (deliveryDate.getDay() === 0) {
      deliveryDate.setDate(deliveryDate.getDate() + 1);
      deliveryDate = this.getNextNonExcludedDate(deliveryDate);
    }

    return deliveryDate;
  }

  getNextDate(date) {
    const nextDate = this.getCurrentDate(date);
    nextDate.setDate(date.getDate() + 1);

    return nextDate;
  }

  getFirstAvailableDate() {
    const today = this.getCurrentDate();
    const firstAvailable = this.getCurrentDate(
      window.swyftStaleUplift(this.stockData?.firstAvailableDate, this.stockData?.updatedAt)
    );
    return isNaN(firstAvailable) || firstAvailable < today
      ? today
      : firstAvailable;
  }

  getStoreCutoffTime() {
    const tomorrow = this.getTomorrowDate();
    const cutoffHour = this.getStoreCutOffHour();
    const cutoffMinute = this.getStoreCutOffMinute();

    if (cutoffHour && cutoffMinute && tomorrow) {
      const cutoffTime = this.getCurrentDate(tomorrow);
      cutoffTime.setHours(cutoffHour, cutoffMinute, 0);
      return cutoffTime;
    }
    return null;
  }

  getStoreCutOffHour() {
    return !isNaN(window.store_cutoff_hour) ? window.store_cutoff_hour : null;
  }

  getStoreCutOffMinute() {
    return !isNaN(window.store_cutoff_minute)
      ? window.store_cutoff_minute
      : null;
  }

  getDeliveryDateText(date) {
    if (!date) return null;

    const day = date.getDate();
    const month = date.toLocaleDateString(this.locale, { month: "short" });
    if (this.formattedDateTemplate && this.formattedDateTemplate !== "") {
      const { day: localizedDay, ordinal } = this.getOrdinalTemplateValues(day);
      let template = this.formattedDateTemplate;
      template = template.replace(/{{\s*day\s*}}/g, localizedDay);
      template = template.replace(/{{\s*ordinal\s*}}/g, ordinal);
      template = template.replace(/{{\s*month\s*}}/g, month);
      return template.replace(/\s{2,}/g, " ").trim();
    }

    const localizedDay = this.getLocalizedOrdinal(day);
    return `${localizedDay} ${month}`;
  }

  getLocalizedOrdinal(day) {
    const lang = this.locale?.split('-')[0];
    switch (lang) {
      case 'de':
        return `${day}.`;

      case 'fr':
        return day === 1 ? '1er' : `${day}e`;

      case 'es':
        return `${day}.º`;

      case 'en':
      default:
        return `${day}${this.getFallbackOrdinalSuffix(day)}`;
    }
  }

  getOrdinalTemplateValues(day) {
    const dayString = String(day);
    const lang = this.locale?.split('-')[0];

    if (lang === 'en') {
      const suffix = this.getFallbackOrdinalSuffix(day);
      return {
        day: dayString,
        ordinal: suffix ? `<sup>${suffix}</sup>` : '',
      };
    }

    if (lang === 'fr') {
      const ordinal = day === 1 ? 'er' : 'e';
      return {
        day: dayString,
        ordinal: `<sup>${ordinal}</sup>`,
      };
    }

    if (lang === 'es') {
      return {
        day: dayString,
        ordinal: '<sup>º</sup>',
      };
    }

    if (lang === 'de') {
      return {
        day: dayString,
        ordinal: '.',
      };
    }

    const localized = this.getLocalizedOrdinal(day);
    const suffix = localized.startsWith(dayString)
      ? localized.slice(dayString.length)
      : '';

    return {
      day: dayString,
      ordinal: suffix,
    };
  }

  getFallbackOrdinalSuffix(day) {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }
}
ItemAvailability.deliveryMessagesCache = undefined;
customElements.define("item-availability", ItemAvailability);

/*
  Stock guard shared by the add-to-cart entry points that post straight to
  /cart/add.js without a server-rendered stock figure on the button (the
  Graphene-rendered collection and search cards). Availability comes from the
  swyft.variant_stock_check metafield, which Graphene renders onto each card as
  .stock-check[data-inventory]. Where no stock figure is present at all — swatches,
  gift cards, free samples — the guard allows the add.
*/
window.swyftStockGuard = {
  fallbackMessage: "You've reached the maximum stock.",
  pending: new Map(),

  /** Translated "you can only add N of this item", as used by the cart. */
  messageFor(stockQty) {
    const template = window.cartStrings && window.cartStrings.quantityError;
    if (template && Number.isFinite(stockQty)) return template.replace("[quantity]", stockQty);

    return this.fallbackMessage;
  },

  /** Read an attribute as a quantity. Absent, blank or non-numeric is unknown. */
  toQty(value) {
    if (value === null || value === undefined) return NaN;

    const trimmed = String(value).trim();
    if (trimmed === "") return NaN;

    return Number(trimmed);
  },

  /**
   * Resolve the metafield stock quantity for a variant from its card markup.
   * NaN means "no figure we can trust for this variant", which allows the add: a
   * card carrying several .stock-check elements is ambiguous, so it counts as
   * unknown rather than blocking against whichever one happens to be first.
   */
  stockQtyFor(button, variantId) {
    if (!button || typeof button.closest !== "function") return NaN;

    const override = this.toQty(button.dataset ? button.dataset.stockQty : null);
    if (Number.isFinite(override)) return override;

    const card = button.closest(".product-card-wrapper, .card-wrapper, .grid__item");
    if (!card) return NaN;

    const scoped = card.querySelector(`.stock-check[data-variant-id="${variantId}"]`);
    if (scoped) return this.toQty(scoped.getAttribute("data-inventory"));

    // Cloned variant cards carry a copy of the source card's .stock-check, so
    // its figure describes a different variant than the one being added.
    if (card.closest('[data-cloned="true"]')) return NaN;

    const candidates = card.querySelectorAll(".stock-check");
    if (candidates.length !== 1) return NaN;

    return this.toQty(candidates[0].getAttribute("data-inventory"));
  },

  /**
   * Sum the quantity of a variant already in the cart, read from the rendered
   * cart rather than /cart.js: the add-to-cart entry points run from an inline
   * onclick, so the check has to stay synchronous to leave their timing alone.
   * Both cart surfaces re-render on every add, so this stays current.
   */
  cartQtyFor(variantId) {
    const scope = document.querySelector("cart-drawer-items, cart-items") || document;
    const inputs = scope.querySelectorAll(`[data-quantity-variant-id="${variantId}"]`);

    let total = 0;
    inputs.forEach((input) => {
      // The value attribute is the quantity the cart was rendered with; the
      // property follows whatever has been typed into the field since.
      const qty = this.toQty(input.getAttribute("value"));
      if (Number.isFinite(qty)) total += qty;
    });

    return total;
  },

  /**
   * Whether adding `quantity` of `variantId` stays within stock. Unknown stock
   * or an unreadable cart allows the add rather than blocking a valid sale.
   */
  allowsAdd({ button, variantId, quantity = 1 }) {
    try {
      const stockQty = this.stockQtyFor(button, variantId);
      if (!Number.isFinite(stockQty)) return { allowed: true };

      const wanted = Number(quantity) || 1;
      const inCart = this.cartQtyFor(variantId);
      const allowed = inCart + wanted <= stockQty;

      if (!allowed) {
        console.warn("[stock-guard] Blocked add", { variantId, stockQty, inCart, quantity: wanted });
      }

      return { allowed, stockQty, inCart };
    } catch (error) {
      console.error("[stock-guard] Check failed, allowing add", error);
      return { allowed: true };
    }
  },

  /**
   * Claim a variant so two quick clicks can't both pass the stock check. The
   * claim expires on its own so a request that never settles can't leave a
   * variant permanently unaddable.
   */
  begin(variantId) {
    const key = String(variantId);
    if (this.pending.has(key)) return false;
    this.pending.set(
      key,
      setTimeout(() => this.pending.delete(key), 15000)
    );
    return true;
  },

  end(variantId) {
    const key = String(variantId);
    clearTimeout(this.pending.get(key));
    this.pending.delete(key);
  },

  /**
   * Show a blocked-add message in the button, then restore it. Only the label's
   * text is swapped: replacing the button's markup would drop the spinner that
   * the add-to-cart paths query without a null check.
   */
  flash(button, message = this.fallbackMessage, duration = 2500) {
    if (!button || button.dataset.stockFlash === "true") return;

    const label = button.querySelector("span:not(.sold-out-message):not(.visually-hidden)") || button;
    const original = label.textContent;

    button.dataset.stockFlash = "true";
    if (!button.hasAttribute("aria-live")) button.setAttribute("aria-live", "polite");
    label.textContent = message;

    setTimeout(() => {
      label.textContent = original;
      delete button.dataset.stockFlash;
    }, duration);
  },
};
